import React, { Fragment, useState } from 'react';
import { useRouteMatch, useHistory } from 'react-router-dom';
import { Button } from '@material-ui/core';
import { Add, Check, Cancel } from '@material-ui/icons';
import { JwlTable, Chip, ErrorComponent, LoadingSpinner } from '../../../../components';
import { userLanguage } from '../../../../core/AuthContext';
import { GQLOperationBuilder } from '../../../../operations/GQLOperationBuilder';

import CreatePlatformSaleCatalogDialog from './CreatePlatformSaleCatalogDialog';
import { GQLParty } from '@jewelutions/core-crm-server-api-client-domain';
import { PlatformNavigation } from '../../navigation/PlatformNavigation';

interface IProps {
  party: GQLParty;
}

const PlatformProducerContractList: React.FC<IProps> = ({ party }) => {
  const [currentDeliveryUnitSaleCatalog, setCurrentDeliveryUnitSaleCatalog] = useState(null);
  const [errorComponentState, setErrorComponentState] = useState(false);
  const [isCreatePlatformSaleCatalogOpen, setIsCreatePlatformSaleCatalogOpen] = useState(false);
  const history = useHistory();

  const match: any = useRouteMatch();

  const [
    approvePlatformProducerContractChangeRequest,
    {}
  ] = GQLOperationBuilder.request.approvePlatformProducerContractChangeRequest();

  const [
    createPlatformSaleCatalogFromDeliveryUnitSaleCatalog,
    {}
  ] = GQLOperationBuilder.platformSaleCatalog.createFromDeliveryUnitSaleCatalog();

  const {
    data,
    loading,
    error,
    networkStatus,
    refetch: refetchData
  } = GQLOperationBuilder.deliveryUnitSaleCatalog.getDeliveryUnitSaleCatalogWithPlatformProducerContracts(
    {
      includeOnlyActiveContracts: true
    },
    { fetchPolicy: 'network-only' }
  );

  const {
    data: requestsData,
    loading: requestsLoading,
    error: requestsError,
    refetch: refetchRequests
  } = GQLOperationBuilder.request.getPlatformProducerContractChangeRequestByPlatform(
    {
      completed: false
    },
    { fetchPolicy: 'network-only' }
  );

  const reloadData = async () => {
    await refetchData();
    await refetchRequests();
  };

  if (loading || requestsLoading) return <LoadingSpinner />;

  if (error && !errorComponentState)
    return (
      <ErrorComponent
        close={setErrorComponentState}
        details={error?.stack}
        message={error.message}
        callback={refetchData}
      />
    );
  const requests = GQLOperationBuilder.request.requestArrayFromResponse(requestsData);

  const getProducerContractsRows = () => {
    return GQLOperationBuilder.deliveryUnitSaleCatalog
      .deliveryUnitSaleCatalogWithPlatformProducerContractArrayFromResponse(data)
      .map(producerContract => {
        return {
          id: producerContract?.platformProducerContract?.id,
          cells: [
            producerContract?.platformProducerContract?.producerParty?.name,
            producerContract?.platformProducerContract?.producerDeliveryUnit?.name,
            producerContract?.deliveryUnitSaleCatalog?.name[userLanguage],
            producerContract?.deliveryUnitSaleCatalog?.description[userLanguage],
            <Chip
              size="small"
              mr={1}
              mb={1}
              label={producerContract.platformProducerContractActive ? 'YES' : 'NO'}
              $active={producerContract.platformProducerContractActive}
            />,
            <Button
              variant="contained"
              color={'primary'}
              onClick={async () => {
                createPlatformSaleCatalogFromDeliveryUnitSaleCatalog({
                  variables: {
                    input: {
                      deliveryUnitSaleCatalogId: producerContract?.deliveryUnitSaleCatalog?.id,
                      name: { en: producerContract?.deliveryUnitSaleCatalog?.description.en },
                      description: { en: producerContract?.deliveryUnitSaleCatalog?.description.en }
                    }
                  }
                }).then(_res => {
                  history.push(PlatformNavigation.platformSaleCatalogs.pathFromParams({}));
                });
              }}
            >
              <Add />
              Create sale catalog
            </Button>
          ]
        };
      });
  };

  const requestsRows = () => {
    return requests.map(request => {
      return {
        id: request.id,
        cells: [
          request.request.platformProducerContractChange?.previousContract.producerParty.name,
          request.request.platformProducerContractChange?.previousContract.producerDeliveryUnit.name,
          new Date(Date.parse(request.createdAt)).toLocaleString(),
          new Date(Date.parse(request.updatedAt)).toLocaleString(),
          new Date(Date.parse(request.validUntil)).toLocaleString(),
          <Button
            variant="contained"
            color={'primary'}
            onClick={async () => {
              approvePlatformProducerContractChangeRequest({
                variables: {
                  accepted: true,
                  approverPartyId: party.id,
                  requestId: request.id,
                  note: 'Approved by platform'
                }
              }).then(_res => {
                reloadData();
              });
            }}
          >
            <Check />
            Approve
          </Button>,
          <Button
            variant="outlined"
            color={'primary'}
            onClick={async () => {
              approvePlatformProducerContractChangeRequest({
                variables: {
                  accepted: false,
                  approverPartyId: party.id,
                  requestId: request.id,
                  note: 'Rejected by platform'
                }
              }).then(_res => {
                reloadData();
              });
            }}
          >
            <Cancel />
            <span>Reject</span>
          </Button>
        ]
      };
    });
  };

  return (
    <Fragment>
      {requests.length > 0 && (
        <JwlTable
          title="Open requests for contract change from producer delivery units"
          description={`The consequence of approving a specific contract change is that a platform sale catalog will be created for the new contract. `}
          columns={[
            'Producer',
            'Delivery Unit',
            'Created at',
            'Updated at',
            'Valid until',
            'Approve',
            'Reject'
          ]}
          rows={requestsRows()}
        />
      )}

      <JwlTable
        title="Platform producer contracts List"
        description="The platform contracts with specific producers / delivery units. Each producer contract defines the producer commitments to the platform in regards with pieces / accessories inventory & producer prices."
        columns={[
          'Producer Name',
          'Producer Delivery Unit Name',
          'Delivery Unit Sale Catalog Name',
          'Delivery Unit Sale Catalog Description',
          'Platform Producer Contract Active',
          'Create platform sale catalog'
        ]}
        rows={getProducerContractsRows()}
      />
      {isCreatePlatformSaleCatalogOpen && (
        <CreatePlatformSaleCatalogDialog
          party={party}
          onClose={val => {
            setIsCreatePlatformSaleCatalogOpen(val);
            refetchData();
          }}
        ></CreatePlatformSaleCatalogDialog>
      )}
    </Fragment>
  );
};

export default PlatformProducerContractList;
